import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plane } from "lucide-react";
import { KineticText } from "@/components/KineticText";

export function SceneFlight() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.2, 1]);
  // plane flies diagonally across the whole scene as you scroll
  const planeX = useTransform(scrollYProgress, [0, 1], ["-10vw", "110vw"]);
  const planeY = useTransform(scrollYProgress, [0, 1], ["18vh", "-8vh"]);
  const planeRot = useTransform(scrollYProgress, [0, 1], [8, -4]);

  return (
    <section ref={ref} data-testid="scene-flight" className="relative h-[130vh] overflow-hidden bg-maroon-deep">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1565444007614-6b38c78224df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Airplane wing above the clouds at sunrise — the journey from India to Portugal"
          className="h-full w-full object-cover"
          style={{ filter: "sepia(0.6) saturate(1.5) hue-rotate(-12deg) brightness(0.92)" }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-maroon-deep/90 via-maroon/25 to-maroon-deep/95 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-b from-maroon-deep/70 via-transparent to-maroon-deep/80" />
      </motion.div>

      {/* flying plane */}
      <motion.div style={{ x: planeX, y: planeY, rotate: planeRot }} className="absolute top-1/3 left-0 z-10 pointer-events-none">
        <Plane className="h-10 w-10 md:h-14 md:w-14 text-gold drop-shadow-lg" strokeWidth={1.4} />
      </motion.div>

      <div className="relative h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-8 w-full">
          <p className="accent-eyebrow text-gold">The journey begins</p>
          <KineticText as="h2" text="A plane lifts off from India —" className="font-playfair text-4xl md:text-6xl lg:text-7xl text-cream mt-6 max-w-4xl leading-[1.03] font-semibold" />
          <KineticText as="h2" text="carrying not luggage, but ambition." className="font-playfair italic text-gradient-gold text-4xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.03]" delay={0.15} />
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3 }} className="text-cream/70 text-lg mt-8 max-w-xl leading-relaxed">
            Above the clouds, past the sunrise, across the Atlantic. This is not tourism. This is transformation — the distance between the life you have and the life you are building.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
